import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AccountService from "../../Services/Account/AccountService";
import { setWebInfo } from "../../Redux/Actions/Index";
import { cacheAuthenticatorEnabled } from "./authenticatorStatusCache";
import Commonservice from "Services/CommonService";
import { Loader } from "Shared/Common/Loader";
import useLang from "Shared/hooks/useLanguage";
import {
  setFaviconAndTitle,
  setValueIntoSessionStorage,
} from "Utils/Common/CommonMethods";

/** Replace with portal page id for Authenticator Settings */
const PAGE_ID = 18;

interface AuthenticatorStatus {
  isConfigured: boolean;
  isEnabled: boolean;
  isSetupInProgress: boolean;
  setupExpiresAt: string;
  lockedUntil: string;
  showSetupButton: boolean;
  showRemoveButton: boolean;
}

const AuthenticatorSettings: React.FC = () => {
  const { t } = useLang();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const labLogo = useSelector((state: any) => state?.Reducer?.labinfo?.logo);
  const [logoUrl, setLogoUrl] = useState(labLogo || "");
  const [status, setStatus] = useState<AuthenticatorStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const getLogoUrl = () => {
    Commonservice.getLoginPageLogo()
      .then((res: any) => {
        const data = res?.data;
        const logo =
          typeof data === "string" ? data : data?.logo || labLogo || "";
        setLogoUrl(logo);
        if (data && typeof data !== "string") {
          dispatch(
            setWebInfo({
              smartLogoUrl: data?.smartLogo,
              title: data?.labName,
            })
          );
          setFaviconAndTitle(data?.smartLogo, data?.labName);
        }
      })
      .catch((err: any) => {
        if (labLogo) setLogoUrl(labLogo);
        console.trace(err);
      });
  };

  const getAuthenticatorStatus = async () => {
    try {
      setStatusLoading(true);
      setStatus(null);
      const res = await AccountService.getAuthenticatorStatus();
      const httpOk = res?.status === 200;
      const payload = res?.data;
      if (
        httpOk &&
        (payload?.isSuccess ||
          payload?.succeeded ||
          payload?.statusCode === 200 ||
          payload?.statusCode === 0)
      ) {
        setStatus(payload.data);
        if (typeof payload?.data?.isEnabled === "boolean") {
          cacheAuthenticatorEnabled(payload.data.isEnabled);
        }
      } else {
        setStatus(null);
        toast.error(
          payload?.message || t("Failed to load authenticator status")
        );
      }
    } catch (err: any) {
      console.trace(err);
      setStatus(null);
      toast.error(
        err?.response?.data?.message || t("Failed to load authenticator status")
      );
    } finally {
      setStatusLoading(false);
    }
  };

  useLayoutEffect(() => {
    setValueIntoSessionStorage("pageId", PAGE_ID);
    if (labLogo) setLogoUrl(labLogo);
    getLogoUrl();
  }, [labLogo]);

  useEffect(() => {
    getAuthenticatorStatus();
  }, []);

  return (
    <div
      className="d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed"
      style={{
        backgroundImage: 'url("/media/illustrations/login/sketch-1.png")',
      }}
    >
      <div
        className="d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20"
        style={{ minHeight: "100vh" }}
      >
        <div
          className="bg-body rounded shadow-sm p-10 p-lg-15 mx-auto"
          style={{ width: 480, maxWidth: "100%" }}
        >
          <a href="#" className="mb-12 d-flex justify-content-center">
            {logoUrl && <img alt="Logo" src={logoUrl} className="h-45px" />}
          </a>
          <div className="text-center mb-10">
            <h3 className="mb-0">{t("Authenticator Settings")}</h3>
          </div>

          {statusLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Loader />
            </div>
          ) : status ? (
            <div className="d-flex flex-column align-items-center gap-4">
              {!status.isEnabled && (
                <button
                  type="button"
                  className="btn btn-primary h-45px"
                  style={{ width: "max-content", whiteSpace: "nowrap" }}
                  onClick={() =>
                    navigate("/enable-authenticator", {
                      state: { isEnabled: false },
                    })
                  }
                  data-test-id="setup-authenticator"
                >
                  {t("Setup Authenticator")}
                </button>
              )}
              {status.isEnabled && (
                <button
                  type="button"
                  className="btn btn-danger h-45px"
                  style={{ width: "max-content", whiteSpace: "nowrap" }}
                  onClick={() =>
                    navigate("/disable-authenticator", {
                      state: { isEnabled: true },
                    })
                  }
                  data-test-id="remove-disable-authenticator"
                >
                  {t("Remove/Delete Authenticator")}
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AuthenticatorSettings;
